import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { queryKeys } from './queryKeys';

export function useUpdateApp() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await authFetch('/api/apps/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update app');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.all });
    },
  });
}

export function useApproveApp() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appId) => {
      const response = await authFetch('/api/apps/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve changes');
      }
      return response.json();
    },
    onSuccess: (data, appId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.all });
    },
  });
}

export function useAddNote() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appId, content, source, noteType }) => {
      const response = await authFetch('/api/build-requests/add-note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId, content, source, noteType }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add note');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(variables.appId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.app(variables.appId) });
    },
  });
}

export function usePayCharge() {
  const { authFetch } = useAuthContext();

  return useMutation({
    mutationFn: async (chargeId) => {
      const response = await authFetch('/api/apps/pay-charge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chargeId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create payment session');
      }
      return response.json();
    },
  });
}

export function usePayFinal() {
  const { authFetch } = useAuthContext();

  return useMutation({
    mutationFn: async (appId) => {
      const response = await authFetch('/api/apps/pay-final', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create payment session');
      }
      return response.json();
    },
  });
}

export function useRemoveRepository() {
  const { authFetch } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (repoId) => {
      const response = await authFetch(`/api/apps/remove-repository?repositoryId=${repoId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to remove repository');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.all });
    },
  });
}
